import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import {
  useCashiers,
  type Cashier,
  type CashierListSort,
  type CashierStatus,
} from '@/hooks/use-employees';
import { isCashierEditable } from './cashier-ownership';
import { useLocations } from '@/hooks/use-locations';
import { useRoles } from '@/hooks/use-roles';

import { CashierGpStores } from './cashier-gp-stores';
import { CashierLoginQrButton } from './cashier-login-qr';
import { useT } from '@/i18n/use-t';

/** Page sizes the operator can pick (RL-610); the endpoint caps at 200. */
export const CASHIER_PAGE_SIZES = [25, 50, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 50;

export function CashiersListPage() {
  const t = useT();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CashierStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  // VL-350: '' = every store / role; 'none' = not store-restricted / no role.
  const [storeFilter, setStoreFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [sort, setSort] = useState<CashierListSort>('code');
  const locationsQuery = useLocations();
  const rolesQuery = useRoles();

  const { data, isLoading, error } = useCashiers({
    search,
    status: statusFilter === 'all' ? undefined : statusFilter,
    locationId: storeFilter === '' ? undefined : storeFilter,
    roleId: roleFilter === '' ? undefined : roleFilter,
    sort,
    page,
    pageSize,
  });

  // Unknown until a page has loaded; a load in flight or a failed load must
  // never be read as "one page", or the pager would snap back to page 1.
  const totalPages = data === undefined ? null : Math.max(1, Math.ceil(data.total / pageSize));

  // The list can shrink under the operator (another tab suspends or the
  // import removes cashiers): never sit on a page past the end.
  useEffect(() => {
    if (totalPages !== null && page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  /** A column header that sorts the list by that column (ascending). */
  const sortHeader = (column: CashierListSort, label: string) => (
    <button
      type="button"
      onClick={() => {
        setSort(column);
        setPage(1);
      }}
      aria-pressed={sort === column}
      className={sort === column ? 'font-semibold text-foreground' : 'hover:text-foreground'}
    >
      {label}
      {sort === column ? ' ↑' : ''}
    </button>
  );

  return (
    <div className="space-y-5">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('employees.list.title', 'Cashiers')}</h1>
          {/* Terminals download this list with the Argon2id PIN hashes through
              `/sync/cashiers`; neither belongs in operator copy (RL-331). */}
          <p className="text-sm text-muted-foreground">
            {data === undefined
              ? '…'
              : t(
                  'employees.list.summary',
                  '{count} cashier(s). Terminals receive this list and the cashiers’ sign-in details automatically.',
                ).replace('{count}', String(data.total))}
          </p>
        </div>
        <Button asChild>
          <Link to="/employees/new">{t('employees.new.title', 'New cashier')}</Link>
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder={t('employees.list.search', 'Search by code or name…')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="max-w-xs"
        />
        <div className="flex items-center gap-1 rounded-md border border-border bg-card p-1 text-sm">
          {(['all', 'active', 'suspended'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => {
                setStatusFilter(opt);
                setPage(1);
              }}
              className={
                statusFilter === opt
                  ? 'rounded bg-primary px-3 py-1 text-primary-foreground'
                  : 'rounded px-3 py-1 text-muted-foreground hover:bg-secondary'
              }
            >
              {opt === 'all'
                ? t('employees.list.filterAll', 'All')
                : opt === 'active'
                  ? t('employees.list.filterActive', 'Active')
                  : t('employees.list.filterSuspended', 'Suspended')}
            </button>
          ))}
        </div>
        <Select
          aria-label={t('employees.list.store', 'Store')}
          className="max-w-xs"
          value={storeFilter}
          onChange={(e) => {
            setStoreFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t('employees.list.allStores', 'All stores')}</option>
          <option value="none">{t('employees.list.noStore', 'Not bound to a store')}</option>
          {(locationsQuery.data?.items ?? []).map((location) => (
            <option key={location.id} value={location.id}>
              {location.code} — {location.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t('employees.list.role', 'User role')}
          className="max-w-xs"
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t('employees.list.allRoles', 'All roles')}</option>
          <option value="none">{t('employees.detail.noRole', 'No role')}</option>
          {(rolesQuery.data ?? []).map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="rounded-md border border-border bg-card">
        <Table>
          <THead>
            <TR>
              <TH>{sortHeader('code', t('employees.list.code', 'Code'))}</TH>
              <TH>{sortHeader('name', t('employees.list.name', 'Name'))}</TH>
              <TH>{sortHeader('store', t('employees.list.store', 'Store'))}</TH>
              <TH>{sortHeader('role', t('employees.list.role', 'User role'))}</TH>
              <TH>{t('employees.field.loginName', 'QR login name')}</TH>
              <TH>{t('employees.gp_stores.title', 'GP stores')}</TH>
              <TH>{t('employees.field.status', 'Status')}</TH>
              <TH>{t('employees.list.pinUpdated', 'PIN updated')}</TH>
              <TH>{t('employees.list.resets', 'Resets')}</TH>
              <TH className="w-56" />
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={10} className="py-8 text-center text-muted-foreground">
                  {t('common.loading', 'Loading…')}
                </TD>
              </TR>
            )}
            {error !== null && (
              <TR>
                <TD colSpan={10} className="py-8 text-center text-destructive">
                  {t('employees.list.loadFailed', 'Failed to load cashiers')}
                </TD>
              </TR>
            )}
            {data?.cashiers.length === 0 && (
              <TR>
                <TD colSpan={10} className="py-8 text-center text-muted-foreground">
                  {t('employees.list.emptyBefore', 'No cashiers yet. Click')}{' '}
                  <em>{t('employees.new.title', 'New cashier')}</em>{' '}
                  {t('employees.list.emptyAfter', 'to provision one.')}
                </TD>
              </TR>
            )}
            {data?.cashiers.map((c) => (
              <TR key={c.id}>
                <TD className="font-mono text-xs">{c.employeeCode}</TD>
                <TD className="font-medium">
                  <Link to={`/employees/${c.id}`} className="hover:underline">
                    {c.displayName}
                  </Link>
                  {!isCashierEditable(c) && (
                    <Badge variant="outline" className="ml-2">
                      {t('employees.managed.badge', 'Store system')}
                    </Badge>
                  )}
                </TD>
                <TD className="text-xs">
                  {c.location === undefined || c.location === null ? (
                    '—'
                  ) : (
                    <>
                      <span className="font-mono">{c.location.code}</span> {c.location.name}
                    </>
                  )}
                </TD>
                <TD className="text-xs">
                  <CashierRoleCell cashier={c} />
                </TD>
                <TD className="font-mono text-xs">{c.loginName ?? '—'}</TD>
                <TD>
                  <CashierGpStores cashier={c} />
                </TD>
                <TD>
                  <StatusBadge cashier={c} />
                </TD>
                <TD className="text-xs text-muted-foreground">{relative(c.pinUpdatedAt, t)}</TD>
                <TD className="text-xs text-muted-foreground">{c.pinResetCount}</TD>
                <TD className="whitespace-nowrap text-right">
                  {/* VL-349: only for holders of employees.admin. */}
                  <CashierLoginQrButton cashier={c} />
                  <Button asChild variant="ghost" size="sm">
                    <Link to={`/employees/${c.id}`}>{t('employees.list.open', 'Open')}</Link>
                  </Button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          {t('employees.list.pageSize', 'Rows per page')}
          <Select
            value={String(pageSize)}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="h-8 w-20"
          >
            {CASHIER_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Select>
        </label>
        <div className="flex items-center gap-3">
          <p className="text-xs text-muted-foreground">
            {t('employees.list.page', 'Page {page} of {pages}')
              .replace('{page}', String(page))
              .replace('{pages}', totalPages === null ? '…' : String(totalPages))}
          </p>
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t('employees.list.prev', 'Prev')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={totalPages === null || page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('employees.list.next', 'Next')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * VL-350: the store system's group (e.g. OP) for an imported cashier — the
 * value the store system shows — with the Back Office role it maps to; the
 * role alone for every other cashier.
 */
function CashierRoleCell({ cashier }: { cashier: Cashier }) {
  const group = cashier.storeGroup ?? null;
  const role = cashier.role?.name ?? null;
  if (group === null && role === null) return <>—</>;
  return (
    <span className="flex flex-col">
      {group !== null && <span className="font-mono">{group}</span>}
      {role !== null && (
        <span className={group === null ? undefined : 'text-muted-foreground'}>{role}</span>
      )}
    </span>
  );
}

function StatusBadge({ cashier }: { cashier: Cashier }) {
  const t = useT();
  if (cashier.status === 'active')
    return <Badge variant="success">{t('employees.status.active', 'active')}</Badge>;
  return <Badge variant="outline">{t('employees.status.suspended', 'suspended')}</Badge>;
}

type Translate = (key: string, bakedEnglish?: string) => string;

function relative(at: string | null, t: Translate): string {
  if (at === null) return t('employees.relative.never', 'never');
  const ms = Date.now() - new Date(at).getTime();
  const n = (value: number): string => String(Math.round(value));
  if (ms < 60_000) return t('employees.relative.seconds', '{n}s ago').replace('{n}', n(ms / 1000));
  if (ms < 3_600_000)
    return t('employees.relative.minutes', '{n}m ago').replace('{n}', n(ms / 60_000));
  if (ms < 86_400_000)
    return t('employees.relative.hours', '{n}h ago').replace('{n}', n(ms / 3_600_000));
  return t('employees.relative.days', '{n}d ago').replace('{n}', n(ms / 86_400_000));
}
